import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, AlertCircle, TrendingUp, TrendingDown, Users, Wallet, Pencil, Save,
} from "lucide-react";

export default function SalaryModulePage() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const salaryFrequency = user?.salary_frequency || "twice_monthly";

  const [incomes, setIncomes] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState({}); // {employee_id: {label_id: {amount, schedule}}}
  const [draft, setDraft] = useState({}); // pending edits keyed same as entries
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openKind, setOpenKind] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!selectedBranch) {
      setIncomes([]); setDeductions([]); setEmployees([]); setEntries({});
      return;
    }
    setLoading(true);
    try {
      const [lbl, emp, ent] = await Promise.all([
        api.get("/salary-labels", { params: { branch_id: selectedBranch.id } }),
        api.get("/employees", { params: { branch_id: selectedBranch.id } }),
        api.get("/salary-entries", { params: { branch_id: selectedBranch.id } }),
      ]);
      setIncomes((lbl.data || []).filter((d) => d.kind === "income"));
      setDeductions((lbl.data || []).filter((d) => d.kind === "deduction"));
      setEmployees(emp.data || []);
      const map = {};
      (ent.data || []).forEach((e) => {
        map[e.employee_id] = map[e.employee_id] || {};
        map[e.employee_id][e.label_id] = { amount: e.amount, schedule: e.schedule || "" };
      });
      setEntries(map);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => { load(); }, [load]);

  const submitLabel = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/salary-labels", { branch_id: selectedBranch.id, kind: openKind, name: name.trim() });
      toast.success(`${openKind === "income" ? "Income" : "Deduction"} label "${name.trim()}" added`);
      setOpenKind(null); setName(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeLabel = async (label) => {
    if (!window.confirm(`Delete "${label.name}"? This also removes its column from Salary Formula and clears amounts for every employee.`)) return;
    try {
      const { data } = await api.delete(`/salary-labels/${label.id}`);
      toast.success(`Deleted "${label.name}" (${data.deleted_formula_cells} formula cells, ${data.deleted_salary_entries} entries cleared)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Stage a local change (no API call) — Save button persists them.
  const stageChange = (employeeId, labelId, patch) => {
    setDraft((prev) => {
      const empDraft = { ...(prev[employeeId] || {}) };
      const existing = empDraft[labelId] || (entries[employeeId] || {})[labelId] || { amount: 0, schedule: "" };
      empDraft[labelId] = { ...existing, ...patch };
      return { ...prev, [employeeId]: empDraft };
    });
  };

  const hasChanges = useMemo(() => Object.keys(draft).length > 0, [draft]);

  const saveAll = async () => {
    if (!hasChanges || !selectedBranch) return;
    setSaving(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const empId of Object.keys(draft)) {
        for (const labelId of Object.keys(draft[empId])) {
          const { amount, schedule } = draft[empId][labelId];
          try {
            await api.put("/salary-entries", {
              branch_id: selectedBranch.id,
              employee_id: empId,
              label_id: labelId,
              amount: Number(amount) || 0,
              schedule: schedule || "",
            });
            ok += 1;
          } catch (e) {
            failed += 1;
            toast.error(formatApiError(e));
          }
        }
      }
      if (ok > 0) toast.success(`Saved ${ok} entr${ok === 1 ? "y" : "ies"}${failed ? `, ${failed} failed` : ""}`);
      setDraft({});
      load();
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => setDraft({});

  // Merge committed entries + draft for rendering.
  const merged = useMemo(() => {
    const out = { ...entries };
    for (const empId of Object.keys(draft)) {
      out[empId] = { ...(entries[empId] || {}), ...draft[empId] };
    }
    return out;
  }, [entries, draft]);

  return (
    <>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Employee Salary Module</h1>
        <p className="text-brand-inkMuted mt-1">
          Income & deduction columns apply to every employee in{" "}
          {selectedBranch ? <span className="font-semibold text-foreground">{selectedBranch.name}</span> : "the selected branch"}.
          Amounts are entered per employee below.
        </p>
      </div>

      {!selectedBranch ? (
        <NoBranchCard />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              onClick={() => { setOpenKind("income"); setName(""); }}
              className="rounded-2xl bg-[#2E7D32] hover:bg-[#1B5E20] text-white shadow-sm"
              data-testid="add-income-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Income
            </Button>
            <Button
              onClick={() => { setOpenKind("deduction"); setName(""); }}
              className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white shadow-sm"
              data-testid="add-deduction-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Deduction
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mb-8">
            <LabelGroup title="Income labels" icon={TrendingUp} tone="green" items={incomes}
              loading={loading} onRemove={removeLabel}
              onEdit={(l) => { setEditTarget(l); setName(l.name); }}
              emptyHint='Click "Add Income" to create a column (e.g., Regular Pay, COLA, Allowance).'
              testIdPrefix="income" />
            <LabelGroup title="Deduction labels" icon={TrendingDown} tone="red" items={deductions}
              loading={loading} onRemove={removeLabel}
              onEdit={(l) => { setEditTarget(l); setName(l.name); }}
              emptyHint='Click "Add Deduction" to create a column (e.g., SSS, PhilHealth, Pag-IBIG).'
              testIdPrefix="deduction" />
          </div>

          <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6" data-testid="employee-amounts-section">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
                  <Users className="w-5 h-5 text-brand-purple" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-foreground">Per-employee amounts</h2>
                  <p className="text-sm text-brand-inkMuted">
                    Edit freely — click <strong>Save</strong> to persist. Currency: <span className="font-semibold text-foreground">{currency}</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {hasChanges && (
                  <Button onClick={discardChanges} variant="outline"
                    className="rounded-2xl border-brand-purple/30"
                    data-testid="discard-changes-btn">
                    Discard
                  </Button>
                )}
                <Button onClick={saveAll} disabled={!hasChanges || saving}
                  className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white disabled:opacity-50"
                  data-testid="save-salary-btn">
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? "Saving..." : hasChanges ? `Save (${Object.values(draft).reduce((s, x) => s + Object.keys(x).length, 0)})` : "Save"}
                </Button>
              </div>
            </div>

            {loading ? (
              <p className="text-brand-inkMuted">Loading...</p>
            ) : employees.length === 0 ? (
              <EmptyHint icon={Users} title="No employees yet" hint="Add employees in Employee List to start entering amounts." />
            ) : incomes.length + deductions.length === 0 ? (
              <EmptyHint icon={Wallet} title="No labels yet" hint='Click "Add Income" or "Add Deduction" above to create columns.' />
            ) : (
              <div className="space-y-5">
                {employees.map((emp) => (
                  <EmployeeAmountsCard
                    key={emp.id}
                    employee={emp}
                    branchName={selectedBranch.name}
                    incomes={incomes}
                    deductions={deductions}
                    values={merged[emp.id] || {}}
                    currency={currency}
                    salaryFrequency={salaryFrequency}
                    onChange={(labelId, patch) => stageChange(emp.id, labelId, patch)}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="edit-label-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Rename {editTarget?.kind === "income" ? "Income" : "Deduction"} Label
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!name.trim() || !editTarget) return;
              setBusy(true);
              try {
                await api.patch(`/salary-labels/${editTarget.id}`, { name: name.trim() });
                toast.success("Label renamed");
                setEditTarget(null); setName("");
                load();
              } catch (err) { toast.error(formatApiError(err)); }
              finally { setBusy(false); }
            }}
            className="space-y-4"
            data-testid="edit-label-form"
          >
            <div>
              <Label className="font-heading">Label name</Label>
              <Input
                autoFocus required value={name} onChange={(e) => setName(e.target.value)}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                data-testid="edit-label-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}
                className="rounded-2xl border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy}
                className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
                data-testid="edit-label-submit">
                {busy ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openKind} onOpenChange={(o) => !o && setOpenKind(null)}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid={`add-${openKind}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Add {openKind === "income" ? "Income" : "Deduction"} Column
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitLabel} className="space-y-4">
            <div>
              <Label className="font-heading">{openKind === "income" ? "Income" : "Deduction"} Label</Label>
              <Input
                autoFocus required value={name} onChange={(e) => setName(e.target.value)}
                placeholder={openKind === "income"
                  ? "Example: Allowance, Bonus, Incentive"
                  : "Example: SSS, PhilHealth, Late Deduction"}
                className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                data-testid={`${openKind}-label-input`}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenKind(null)}
                className="rounded-2xl border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy}
                className={`rounded-2xl text-white ${
                  openKind === "income" ? "bg-[#2E7D32] hover:bg-[#1B5E20]" : "bg-[#C62828] hover:bg-[#B71C1C]"
                }`}
                data-testid={`${openKind}-submit-btn`}>
                {busy ? "Adding..." : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const INCOME_SCHEDULES_TWICE = ["Calculated Daily", "Payable on 1st Half", "Payable on 2nd Half"];
const INCOME_SCHEDULES_MONTHLY = ["Calculated Daily", "Payable Monthly"];
const DEDUCTION_SCHEDULES_TWICE = ["Apply on 1st Half", "Apply on 2nd Half"];
const DEDUCTION_SCHEDULES_MONTHLY = ["Apply Monthly"];

function EmployeeAmountsCard({ employee, branchName, incomes, deductions, values, currency, salaryFrequency, onChange }) {
  const incomeTotal = useMemo(
    () => incomes.reduce((s, l) => s + (Number(getAmount(values[l.id])) || 0), 0),
    [incomes, values]
  );
  const deductionTotal = useMemo(
    () => deductions.reduce((s, l) => s + (Number(getAmount(values[l.id])) || 0), 0),
    [deductions, values]
  );

  const isMonthly = salaryFrequency === "monthly";
  const incomeSchedules = isMonthly ? INCOME_SCHEDULES_MONTHLY : INCOME_SCHEDULES_TWICE;
  const deductionSchedules = isMonthly ? DEDUCTION_SCHEDULES_MONTHLY : DEDUCTION_SCHEDULES_TWICE;

  return (
    <article className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6" data-testid={`employee-amounts-${employee.id}`}>
      <header className="mb-4">
        <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-wide text-foreground uppercase" data-testid={`employee-name-${employee.id}`}>
          {employee.name}
        </h3>
        <p className="text-sm text-brand-inkMuted uppercase tracking-wide">
          {employee.position}{branchName && <span className="lowercase normal-case"> · {branchName}</span>}
        </p>
      </header>

      <div className="grid lg:grid-cols-2 gap-5">
        <SidePanel
          title="INCOME"
          tone="green"
          total={incomeTotal}
          currency={currency}
          labels={incomes}
          values={values}
          scheduleOptions={incomeSchedules}
          defaultSchedule={incomeSchedules[0]}
          onChange={onChange}
          testIdPrefix={`income-${employee.id}`}
        />
        <SidePanel
          title="DEDUCTION"
          tone="red"
          total={deductionTotal}
          currency={currency}
          labels={deductions}
          values={values}
          scheduleOptions={deductionSchedules}
          defaultSchedule={deductionSchedules[0]}
          fixedSchedule={isMonthly}
          onChange={onChange}
          testIdPrefix={`deduction-${employee.id}`}
        />
      </div>
    </article>
  );
}

function getAmount(v) {
  if (v == null) return "";
  if (typeof v === "object") return v.amount ?? "";
  return v;
}
function getSchedule(v) {
  if (v == null) return "";
  if (typeof v === "object") return v.schedule ?? "";
  return "";
}

function SidePanel({ title, tone, total, currency, labels, values, scheduleOptions, defaultSchedule, fixedSchedule, onChange, testIdPrefix }) {
  const styles = tone === "green"
    ? { bg: "bg-[#E8F5E9]/40", border: "border-[#2E7D32]/30", text: "text-[#2E7D32]", trash: "text-[#2E7D32] hover:text-[#1B5E20]" }
    : { bg: "bg-[#FFEBEE]/50", border: "border-[#C62828]/30", text: "text-[#C62828]", trash: "text-[#C62828] hover:text-[#B71C1C]" };

  // Pick a valid schedule: existing value if still allowed, else fall back to defaultSchedule
  const pickSchedule = (raw) => {
    if (raw && scheduleOptions.includes(raw)) return raw;
    return defaultSchedule;
  };

  return (
    <section className={`${styles.bg} border ${styles.border} rounded-3xl p-5`} data-testid={testIdPrefix}>
      <div className="flex items-center justify-between mb-4">
        <h4 className={`font-heading font-semibold tracking-wider ${styles.text}`}>{title}</h4>
        <div className={`font-heading font-semibold ${styles.text}`}>
          {currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>

      {labels.length === 0 ? (
        <p className="text-sm text-brand-inkMuted">No {title.toLowerCase()} labels yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {labels.map((l) => (
            <LabelEntry
              key={l.id}
              label={l}
              currency={currency}
              tone={tone}
              styles={styles}
              amount={getAmount(values[l.id])}
              schedule={pickSchedule(getSchedule(values[l.id]))}
              scheduleOptions={scheduleOptions}
              fixedSchedule={fixedSchedule}
              onChange={onChange}
              testIdPrefix={`${testIdPrefix}-${l.id}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LabelEntry({ label, currency, tone, styles, amount, schedule, scheduleOptions, fixedSchedule, onChange, testIdPrefix }) {
  const [localAmount, setLocalAmount] = useState(amount === "" ? "" : String(amount));

  useEffect(() => {
    const next = amount === "" ? "" : String(amount);
    setLocalAmount(next);
  }, [amount]);

  const inputBorder = tone === "green" ? "border-[#2E7D32]/50" : "border-[#C62828]/50";

  const commitAmount = (v) => {
    // Always include the currently-displayed schedule in the patch — not
    // just when `fixedSchedule` is true. Previously a brand-new per-employee
    // entry rendered a default schedule in the dropdown (via pickSchedule)
    // but only persisted that default if the user actually clicked the
    // dropdown. Typing an amount and clicking Save would write
    // `{amount: 575, schedule: ""}` — and the payslip then silently dropped
    // the entry because "" is not in the allowed schedule whitelist.
    // Bug reported on 2026-06-09 against schoolboss.ph.
    const patch = { schedule };
    if (v === "" || v == null) { onChange(label.id, { ...patch, amount: 0 }); return; }
    const num = Number(v);
    if (Number.isNaN(num)) {
      toast.error(`"${label.name}" must be a number`);
      return;
    }
    onChange(label.id, { ...patch, amount: num });
  };

  return (
    <div data-testid={testIdPrefix}>
      <div className="mb-1.5">
        <span className="font-heading font-semibold text-foreground text-sm">{label.name}</span>
      </div>
      {fixedSchedule ? (
        <div
          className={`w-full h-10 rounded-2xl border ${inputBorder} bg-white px-3 text-sm font-body text-foreground flex items-center mb-2`}
          data-testid={`${testIdPrefix}-schedule-fixed`}
        >
          {schedule}
        </div>
      ) : (
        <select
          value={schedule}
          onChange={(e) => onChange(label.id, { schedule: e.target.value })}
          className={`w-full h-10 rounded-2xl border ${inputBorder} bg-white px-3 text-sm font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple mb-2`}
          data-testid={`${testIdPrefix}-schedule`}
        >
          {scheduleOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}
      <div className={`flex items-stretch rounded-2xl border ${inputBorder} bg-white overflow-hidden`}>
        <span className={`px-3 flex items-center text-sm font-semibold ${styles.text}`}>
          {currency}
        </span>
        <Input
          inputMode="decimal"
          value={localAmount}
          onChange={(e) => { setLocalAmount(e.target.value); commitAmount(e.target.value); }}
          placeholder="0.00"
          className="h-10 border-0 rounded-none text-right font-body text-base focus-visible:ring-0"
          data-testid={`${testIdPrefix}-amount`}
        />
      </div>
    </div>
  );
}

function LabelGroup({ title, icon: Icon, tone, items, loading, onRemove, onEdit, emptyHint, testIdPrefix }) {
  const toneStyles = tone === "green"
    ? { ring: "border-[#2E7D32]/30", iconBg: "bg-[#E8F5E9] text-[#2E7D32]", chip: "bg-[#E8F5E9] text-[#2E7D32]" }
    : { ring: "border-[#C62828]/30", iconBg: "bg-[#FFEBEE] text-[#C62828]", chip: "bg-[#FFEBEE] text-[#C62828]" };

  return (
    <section className={`bg-white rounded-3xl border ${toneStyles.ring} shadow-sm p-6`} data-testid={`${testIdPrefix}-group`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${toneStyles.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">{title}</h2>
          <p className="text-xs text-brand-inkMuted">Applies to every employee in this branch</p>
        </div>
      </div>

      {loading ? (
        <p className="text-brand-inkMuted">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-brand-inkMuted" data-testid={`${testIdPrefix}-empty`}>{emptyHint}</p>
      ) : (
        <ul className="space-y-2" data-testid={`${testIdPrefix}-list`}>
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between rounded-2xl bg-brand-cream border border-brand-purple/10 px-4 py-2.5"
              data-testid={`${testIdPrefix}-item-${it.id}`}>
              <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${toneStyles.chip} mr-3`}>
                {it.name}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => onEdit(it)} className="text-brand-inkMuted hover:text-brand-purple p-1"
                  aria-label={`Edit ${it.name}`} data-testid={`${testIdPrefix}-edit-${it.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => onRemove(it)} className="text-brand-inkMuted hover:text-[#C62828] p-1"
                  aria-label={`Delete ${it.name}`} data-testid={`${testIdPrefix}-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyHint({ icon: Icon, title, hint }) {
  return (
    <div className="text-center py-8" data-testid="amounts-empty">
      <div className="w-12 h-12 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-brand-purple" />
      </div>
      <p className="font-heading font-semibold text-foreground">{title}</p>
      <p className="text-sm text-brand-inkMuted mt-1">{hint}</p>
    </div>
  );
}

function NoBranchCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">Salary labels are scoped per branch. Use the dropdown in the header.</p>
    </div>
  );
}
